import React, { useState } from 'react';
import { register } from '../api/api';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Eye,
  EyeOff,
  Loader2,
  User,
  Mail,
  Lock,
  UserPlus,
  Rocket,
  Workflow,
  Shield,
  ChevronRight,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import AuthSplitLayout from './AuthSplitLayout';
import { normalizeApiError } from '../lib/i18n';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(name, email, password);
      navigate('/login', { state: { message: t('auth.register.success') } });
    } catch (err) {
      setError(normalizeApiError(err, t, 'errors.registrationFailed'));
    }
    setLoading(false);
  };

  return (
    <AuthSplitLayout
      badge={t('auth.register.badge')}
      title={t('auth.register.title')}
      description={t('auth.register.description')}
      sideLabel={t('auth.register.sideLabel')}
      sideTitle={t('auth.register.sideTitle')}
      sideDescription={t('auth.register.sideDescription')}
      stats={[
        { value: '3 min', label: t('auth.register.quickSetup') },
        { value: 'Teams', label: t('auth.register.readyToScale') },
        { value: 'Secure', label: t('auth.register.defaultPosture') },
      ]}
      highlights={[
        {
          icon: Rocket,
          title: t('auth.register.fasterOnboardingTitle'),
          description: t('auth.register.fasterOnboardingDescription'),
        },
        {
          icon: Workflow,
          title: t('auth.register.nextStepsTitle'),
          description: t('auth.register.nextStepsDescription'),
        },
        {
          icon: Shield,
          title: t('auth.register.trustTitle'),
          description: t('auth.register.trustDescription'),
        },
      ]}
      quote={t('auth.register.quote')}
      quoteAuthor={t('auth.register.quoteAuthor')}
      quoteRole={t('auth.register.quoteRole')}
    >
      <div className="rounded-[26px] border border-slate-200/80 bg-white/92 p-5 shadow-[0_20px_40px_rgba(15,23,42,0.06)] lg:p-4 xl:p-6">
        <div className="mb-5 flex items-start justify-between gap-4 lg:mb-4">
          <div>
            <p className="text-sm font-semibold text-slate-950">{t('auth.register.cardTitle')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('auth.register.cardDescription')}</p>
          </div>
          <div className="rounded-2xl bg-sky-50 px-3 py-2 text-xs font-medium text-sky-700 lg:px-2 lg:py-1">
            {t('auth.register.guidedSetup')}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 lg:gap-3">
          <div className="grid gap-2 lg:gap-1">
            <Label htmlFor="name" className="text-slate-700 text-xs sm:text-sm lg:text-xs">{t('auth.register.fullName')}</Label>
            <div className="relative">
              <User className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-11 pr-4 text-slate-950 placeholder:text-slate-400 focus-visible:ring-sky-500 lg:h-10 lg:text-sm"
              />
            </div>
          </div>

          <div className="grid gap-2 lg:gap-1">
            <Label htmlFor="email" className="text-slate-700 text-xs sm:text-sm lg:text-xs">{t('auth.register.workEmail')}</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-11 pr-4 text-slate-950 placeholder:text-slate-400 focus-visible:ring-sky-500 lg:h-10 lg:text-sm"
              />
            </div>
          </div>

          <div className="grid gap-2 lg:gap-1">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="password" className="text-slate-700 text-xs sm:text-sm lg:text-xs">{t('auth.register.password')}</Label>
              <span className="text-xs font-medium text-slate-400">{t('auth.register.passwordHint')}</span>
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-11 pr-12 text-slate-950 focus-visible:ring-sky-500 lg:h-10 lg:text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
                aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50 text-red-900">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" className="h-12 w-full rounded-2xl bg-slate-950 font-semibold text-white shadow-[0_18px_35px_rgba(15,23,42,0.18)] transition-all hover:bg-slate-800 lg:h-10 lg:text-sm" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? t('auth.register.submitting') : t('auth.register.submit')}
          </Button>
        </form>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:mt-3 lg:p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-white lg:h-9 lg:w-9">
              <UserPlus className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{t('auth.register.nextTitle')}</p>
              <p className="text-sm text-slate-600 lg:text-xs lg:leading-5">{t('auth.register.nextDescription')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between lg:gap-2">
        <p>
          {t('auth.register.alreadyHaveAccount')}{' '}
          <Link to="/login" className="font-semibold text-slate-950 transition-colors hover:text-sky-700">
            {t('auth.login.cardTitle')}
          </Link>
        </p>
        <span className="inline-flex items-center text-sm font-medium text-slate-500">
          {t('auth.register.secureOnboarding')} <ChevronRight className="ml-1 h-4 w-4" />
        </span>
      </div>
    </AuthSplitLayout>
  );
}
